import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import {
  type DownloadCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesDownloadPostData as DownloadCollectionImagesData,
  type DownloadCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesDownloadPostError as DownloadCollectionImagesError,
  downloadCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesDownloadPost as downloadCollectionImagesZip,
} from '../../generated';
import type { ZipDownload } from '../download-pdp-images';

/** Images in one zip; a bigger collection comes in parts, a bigger selection is refused (SDD D49, D50). */
export const COLLECTION_ZIP_MAX_IMAGES = 200;

export type DownloadCollectionImagesMutationVariables = DownloadCollectionImagesData['path'] & {
  /** Images to download. Leave unset to download the whole collection. */
  image_ids?: string[];
  /** Whole collection only: 0, 200, 400... for the next part. */
  offset?: number;
};

/** The zip; its blob is `null` when the API answered `204`: an empty part, or none of its originals are in storage. */
export type DownloadCollectionImagesMutation = ZipDownload;

function readTotalCount(headers: Headers) {
  const value = Number(headers.get('Vizit-Total-Count'));
  return headers.has('Vizit-Total-Count') && Number.isFinite(value) ? value : null;
}

async function downloadCollectionImages({
  workspace_id,
  collection_id,
  image_ids,
  offset,
}: DownloadCollectionImagesMutationVariables): Promise<DownloadCollectionImagesMutation> {
  const { data, response } = await downloadCollectionImagesZip({
    path: { workspace_id, collection_id },
    // No body downloads the whole collection; `offset` only goes with it
    body: image_ids ? { image_ids } : undefined,
    query: image_ids || !offset ? undefined : { offset },
    parseAs: 'blob',
    throwOnError: true,
  });
  return {
    // `204` still says how many images the whole download has, so the other parts can come
    // The spec types the `204` as no body
    blob: response.status === 204 || !data ? null : data,
    contentDisposition: response.headers.get('Vizit-Content-Disposition'),
    totalCount: readTotalCount(response.headers),
  };
}

/**
 * Downloads images of a collection as one zip (SDD 3.1.1), originals only, Spark images too (D73): the selected ones
 * with `image_ids` (up to 200), or the whole collection without them, 200 per part, `offset` picks the part and
 * `totalCount` says how many images there are in all (D50, D51).
 *
 * Errors throw, read the code with `getAPIErrorCode`: `422` `DOWNLOAD_LIMIT_EXCEEDED`, `IMAGES_NOT_DOWNLOADABLE`,
 * `INVALID_OFFSET`, `OFFSET_NOT_SUPPORTED`.
 *
 * @example
 * const { mutateAsync: downloadCollectionImages } = useDownloadCollectionImagesMutation();
 * const zip = await downloadCollectionImages({ workspace_id, collection_id, image_ids });
 */
export const useDownloadCollectionImagesMutation = <TError = DownloadCollectionImagesError, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<DownloadCollectionImagesMutation, TError, DownloadCollectionImagesMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<DownloadCollectionImagesMutation, TError, DownloadCollectionImagesMutationVariables, TContext>({
    mutationKey: ['DownloadCollectionImages'],
    mutationFn: downloadCollectionImages,
    // The zip is handed to the browser right away, no need to keep up to 200 originals in the cache
    gcTime: 0,
    ...options,
  });
};
